/**
 * Minimal, dependency-free CSV helpers.
 *
 * The voucher importer needs to read files produced by Excel, Google Sheets and
 * plain text editors, so the parser follows RFC 4180: comma-separated fields,
 * double-quoted fields, doubled quotes as an escape and either CRLF or LF line
 * endings. A leading UTF-8 BOM (added by Excel) is ignored.
 */

/** Parse CSV text into a matrix of string cells. Handles quotes and newlines. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;

  const input = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  let i = 0;
  while (i < input.length) {
    const char = input[i];

    if (inQuotes) {
      if (char === '"') {
        if (input[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      field += char;
      i += 1;
      continue;
    }

    if (char === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }
    if (char === ',') {
      row.push(field);
      field = '';
      i += 1;
      continue;
    }
    if (char === '\r' || char === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
      i += char === '\r' && input[i + 1] === '\n' ? 2 : 1;
      continue;
    }

    field += char;
    i += 1;
  }

  // Flush the trailing field/row unless the file ended on a line break.
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  // Drop fully blank lines so a trailing newline doesn't become an empty row.
  return rows.filter((r) => r.some((cell) => cell.trim() !== ''));
}

/** Escape a single cell for CSV output. */
export function csvEscape(value: unknown): string {
  const str = value === null || value === undefined ? '' : String(value);
  return /[",\r\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
}

/** Serialize a matrix of values into CRLF-delimited CSV text. */
export function toCsv(rows: unknown[][]): string {
  return rows.map((row) => row.map(csvEscape).join(',')).join('\r\n');
}

/** Trigger a browser download for a text payload (CSV by default). */
export function downloadTextFile(
  filename: string,
  content: string,
  mime = 'text/csv;charset=utf-8;',
): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
